import { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Card,
  SimpleGrid,
  Table,
  Badge,
  Button,
  Input,
  Select,
  Spinner,
  Avatar,
  Flex,
  Icon,
  Switch,
  Menu,
  IconButton,
  Stat,
  Tag,
  Field,
  Separator,
  Alert,
  createListCollection
} from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogBackdrop,
  DialogCloseTrigger,
} from './ui/dialog';
import { 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaEllipsisV, 
  FaUserPlus, 
  FaFilter,
  FaUsers,
  FaUserCheck,
  FaCrown,
  FaDownload,
  FaEye
} from 'react-icons/fa';
import { useUsers } from '../hooks/useUsers';
import { adminAPI } from '../services/api';
import { useChangeUserPassword } from '../hooks/useUsers';
import { useSubscriptionTiers } from '../hooks/useSubscriptions';
import { toaster } from './ui/toaster';
import { useTranslation } from 'react-i18next';

const getUserStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active': return 'green';
    case 'inactive': return 'red';
    case 'pending': return 'yellow';
    case 'suspended': return 'orange';
    default: return 'gray';
  }
};

const getSubscriptionColor = (tier: string) => {
  switch (tier) {
    case 'BUSINESS': return 'purple';
    case 'PRO': return 'blue';
    case 'STARTER': return 'teal';
    case 'FREE_TRIAL': return 'gray';
    default: return 'gray';
  }
};

const getTierLabel = (tier: string, t: (key: string, options?: any) => string, fallback?: string) => {
  switch (tier) {
    case 'FREE_TRIAL': return t('admin.content.common.tiers.free');
    case 'STARTER': return t('admin.content.common.tiers.starter');
    case 'PRO': return t('admin.content.common.tiers.pro');
    case 'BUSINESS': return t('admin.content.common.tiers.business');
    default: return fallback || tier || t('admin.content.common.tiers.free');
  }
};

const UserEditModal = ({ 
  isOpen, 
  onClose, 
  user, 
  onUpdate 
}: {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onUpdate: (userId: number, updates: any) => void;
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    is_active: true,
    is_superuser: false,
    subscription_tier: 'FREE_TRIAL',
    subscription_status: 'active',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const { tiers, tierLabels } = useSubscriptionTiers();

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        organization: user.organization || '',
        is_active: user.is_active ?? true,
        is_superuser: user.is_superuser ?? false,
        subscription_tier: user.subscription_tier || 'FREE_TRIAL',
        subscription_status: user.subscription_status || 'active',
        status: user.status || 'active'
      });
    }
  }, [user]);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await onUpdate(user.id, formData);
      onClose();
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="md" placement="center" scrollBehavior="inside">
      <DialogBackdrop />
      <DialogContent 
        maxH="90vh" 
        position="fixed" 
        top="50%" 
        left="50%" 
        transform="translate(-50%, -50%)"
      >
        <DialogHeader>{t('admin.content.userManagement.editUserTitle', { email: user?.email })}</DialogHeader>
        <DialogCloseTrigger />
        <DialogBody maxH="calc(90vh - 180px)" overflowY="auto">
          <VStack gap={4} align="stretch">
            {/* Read-only info banner */}
            <Box bg="gray.50" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
              <SimpleGrid columns={2} gap={2}>
                <VStack align="start" gap={0}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">{t('admin.content.userManagement.fields.userId')}</Text>
                  <Text fontSize="sm">{user?.id}</Text>
                </VStack>
                <VStack align="start" gap={0}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">{t('admin.content.userManagement.fields.verified')}</Text>
                  <Badge colorPalette={user?.is_verified ? 'green' : 'red'} variant="subtle" fontSize="xs">
                    {user?.is_verified ? t('admin.content.common.yes') : t('admin.content.common.no')}
                  </Badge>
                </VStack>
                <VStack align="start" gap={0}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">{t('admin.content.userManagement.fields.created')}</Text>
                  <Text fontSize="sm">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : t('admin.content.common.na')}</Text>
                </VStack>
                <VStack align="start" gap={0}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">{t('admin.content.userManagement.fields.lastLogin')}</Text>
                  <Text fontSize="sm">{user?.last_login ? new Date(user.last_login).toLocaleString() : t('admin.content.common.never')}</Text>
                </VStack>
                {user?.subscription_started_at && (
                  <VStack align="start" gap={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">{t('admin.content.userManagement.fields.subscriptionStart')}</Text>
                    <Text fontSize="sm">{new Date(user.subscription_started_at).toLocaleDateString()}</Text>
                  </VStack>
                )}
                {user?.subscription_ends_at && (
                  <VStack align="start" gap={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">{t('admin.content.userManagement.fields.subscriptionEnd')}</Text>
                    <Text fontSize="sm">{new Date(user.subscription_ends_at).toLocaleDateString()}</Text>
                  </VStack>
                )}
              </SimpleGrid>
            </Box>

            <Separator />

            {/* Personal Info */}
            <Text fontWeight="semibold" fontSize="sm" color="gray.600">{t('admin.content.userManagement.sections.personalInformation')}</Text>
            <Field.Root>
              <Field.Label>{t('admin.content.userManagement.fields.email')}</Field.Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Field.Root>
            <HStack gap={4}>
              <Field.Root flex={1}>
                <Field.Label>{t('admin.content.userManagement.fields.firstName')}</Field.Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              </Field.Root>
              <Field.Root flex={1}>
                <Field.Label>{t('admin.content.userManagement.fields.lastName')}</Field.Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </Field.Root>
            </HStack>
            <Field.Root>
              <Field.Label>{t('admin.content.userManagement.fields.organization')}</Field.Label>
              <Input
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                placeholder={t('admin.content.common.optional')}
              />
            </Field.Root>

            <Separator />

            {/* Account Status */}
            <Text fontWeight="semibold" fontSize="sm" color="gray.600">{t('admin.content.userManagement.sections.accountSettings')}</Text>
            <Field.Root>
              <Field.Label>{t('admin.content.userManagement.fields.accountStatus')}</Field.Label>
              <Select.Root
                value={[formData.status]}
                onValueChange={(details) => setFormData({ ...formData, status: details.value[0] })}
                collection={createListCollection({
                  items: [
                    { value: 'active', label: t('admin.content.common.status.active') },
                    { value: 'suspended', label: t('admin.content.common.status.suspended') },
                    { value: 'pending_verification', label: t('admin.content.common.status.pendingVerification') }
                  ]
                })}
              >
                <Select.Trigger>
                  <Select.ValueText placeholder={t('admin.content.common.selectStatus')} />
                </Select.Trigger>
                <Select.Content>
                  {createListCollection({
                    items: [
                      { value: 'active', label: t('admin.content.common.status.active') },
                      { value: 'suspended', label: t('admin.content.common.status.suspended') },
                      { value: 'pending_verification', label: t('admin.content.common.status.pendingVerification') }
                    ]
                  }).items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Field.Root>
            <Field.Root display="flex" alignItems="center" justifyContent="space-between">
              <Field.Label htmlFor="active" mb="0">
                {t('admin.content.userManagement.fields.activeUser')}
              </Field.Label>
              <Switch.Root
                id="active"
                checked={formData.is_active}
                onCheckedChange={(details) => setFormData({ ...formData, is_active: details.checked })}
              >
                <Switch.HiddenInput />
                <Switch.Control />
              </Switch.Root>
            </Field.Root>
            <Field.Root display="flex" alignItems="center" justifyContent="space-between">
              <Field.Label htmlFor="admin" mb="0">
                {t('admin.content.userManagement.fields.adminUser')}
              </Field.Label>
              <Switch.Root
                id="admin"
                checked={formData.is_superuser}
                onCheckedChange={(details) => setFormData({ ...formData, is_superuser: details.checked })}
              >
                <Switch.HiddenInput />
                <Switch.Control />
              </Switch.Root>
            </Field.Root>

            <Separator />

            {/* Subscription */}
            <Text fontWeight="semibold" fontSize="sm" color="gray.600">{t('admin.content.userManagement.sections.subscription')}</Text>
            <Field.Root>
              <Field.Label>{t('admin.content.userManagement.fields.subscriptionTier')}</Field.Label>
              <Select.Root
                value={[formData.subscription_tier]}
                onValueChange={(details) => setFormData({ ...formData, subscription_tier: details.value[0] })}
                collection={createListCollection({
                  items: tiers.map((tier) => ({ value: tier, label: getTierLabel(tier, t, tierLabels[tier]) }))
                })}
              >
                <Select.Trigger>
                  <Select.ValueText placeholder={t('admin.content.common.selectPlan')} />
                </Select.Trigger>
                <Select.Content>
                  {tiers.map((tier) => (
                    <Select.Item key={tier} item={{ value: tier, label: getTierLabel(tier, t, tierLabels[tier]) }}>
                      {getTierLabel(tier, t, tierLabels[tier])}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Field.Root>
            <Field.Root>
              <Field.Label>{t('admin.content.userManagement.fields.subscriptionStatus')}</Field.Label>
              <Select.Root
                value={[formData.subscription_status]}
                onValueChange={(details) => setFormData({ ...formData, subscription_status: details.value[0] })}
                collection={createListCollection({
                  items: [
                    { value: 'active', label: t('admin.content.common.status.active') },
                    { value: 'inactive', label: t('admin.content.common.status.inactive') },
                    { value: 'pending', label: t('admin.content.common.status.pending') },
                    { value: 'suspended', label: t('admin.content.common.status.suspended') }
                  ]
                })}
              >
                <Select.Trigger>
                  <Select.ValueText placeholder={t('admin.content.common.selectStatus')} />
                </Select.Trigger>
                <Select.Content>
                  {createListCollection({
                    items: [
                      { value: 'active', label: t('admin.content.common.status.active') },
                      { value: 'inactive', label: t('admin.content.common.status.inactive') },
                      { value: 'pending', label: t('admin.content.common.status.pending') },
                      { value: 'suspended', label: t('admin.content.common.status.suspended') }
                    ]
                  }).items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Field.Root>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('admin.content.common.cancel')}
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleUpdate}
            loading={loading}
          >
            {t('admin.content.userManagement.actions.updateUser')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

type SortField = 'email' | 'is_active' | 'subscription_tier' | 'is_superuser' | 'last_login';
type SortDirection = 'asc' | 'desc';

export const UserManagement = () => {
  const { t } = useTranslation();
  const { data: users, isLoading: loading, error, refetch } = useUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('last_login');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const changePasswordMutation = useChangeUserPassword();
  const { tiers, tierLabels } = useSubscriptionTiers();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'last_login' ? 'desc' : 'asc');
    }
  };

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredUsers = users?.filter((user: any) => {
    const matchesSearch = !searchTerm || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || 
      (user.is_active ? 'active' : 'inactive') === statusFilter;
    
    const matchesTier = !tierFilter || user.subscription_tier === tierFilter;
    
    return matchesSearch && matchesStatus && matchesTier;
  })?.sort((a: any, b: any) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'email':
        return dir * (a.email || '').localeCompare(b.email || '');
      case 'is_active':
        return dir * (Number(a.is_active) - Number(b.is_active));
      case 'subscription_tier': {
        const tierOrder: Record<string, number> = { free: 0, professional: 1, enterprise: 2 };
        return dir * ((tierOrder[a.subscription_tier] ?? -1) - (tierOrder[b.subscription_tier] ?? -1));
      }
      case 'is_superuser':
        return dir * (Number(a.is_superuser) - Number(b.is_superuser));
      case 'last_login': {
        const aTime = a.last_login ? new Date(a.last_login).getTime() : 0;
        const bTime = b.last_login ? new Date(b.last_login).getTime() : 0;
        return dir * (aTime - bTime);
      }
      default:
        return 0;
    }
  }) || [];

  const handleUpdateUser = async (userId: number, updates: any) => {
    try {
      await adminAPI.updateUser(userId, updates);
      await refetch();
      toaster.create({
        title: t('admin.content.userManagement.toasts.userUpdatedTitle'),
        description: t('admin.content.userManagement.toasts.userUpdatedDescription'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.content.userManagement.toasts.updateFailedTitle'),
        description: t('admin.content.userManagement.toasts.updateFailedDescription'),
        type: 'error',
      });
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!confirm(t('admin.content.userManagement.confirmDeleteUser'))) return;
    
    try {
      await adminAPI.deleteUser(userId);
      await refetch();
      toaster.create({
        title: t('admin.content.userManagement.toasts.userDeletedTitle'),
        description: t('admin.content.userManagement.toasts.userDeletedDescription'),
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: t('admin.content.common.deleteFailed'),
        description: t('admin.content.userManagement.toasts.deleteUserFailedDescription'),
        type: 'error',
      });
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordChangeUser || !newPassword) return;

    if (newPassword.length < 8) {
      toaster.create({
        title: t('admin.content.userManagement.toasts.passwordTooShortTitle'),
        description: t('admin.content.userManagement.toasts.passwordTooShortDescription'),
        type: 'warning',
      });
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        userId: passwordChangeUser.id,
        newPassword: newPassword
      });
      
      toaster.create({
        title: t('admin.content.userManagement.toasts.passwordChangedTitle'),
        description: t('admin.content.userManagement.toasts.passwordChangedDescription', { email: passwordChangeUser.email }),
        type: 'success',
      });
      
      setIsPasswordOpen(false);
      setNewPassword('');
    } catch (error: any) {
      toaster.create({
        title: t('admin.content.userManagement.toasts.passwordChangeFailedTitle'),
        description: error.response?.data?.detail || t('admin.content.userManagement.toasts.passwordChangeFailedDescription'),
        type: 'error',
      });
    }
  };

  const openEditModal = (user: any) => {
    setSelectedUser(user);
    setIsOpen(true);
  };

  const openPasswordChange = (user: any) => {
    setPasswordChangeUser(user);
    setNewPassword('');
    setIsPasswordOpen(true);
  };

  const userStats = {
    total: users?.length || 0,
    active: users?.filter((u: any) => u.is_active).length || 0,
    admins: users?.filter((u: any) => u.is_superuser).length || 0,
    premium: users?.filter((u: any) => u.subscription_tier === 'PRO' || u.subscription_tier === 'BUSINESS').length || 0
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <VStack gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('admin.content.userManagement.loadingUsers')}</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Title>{(error as any)?.message || t('admin.content.userManagement.errors.failedToLoadUsers')}</Alert.Title>
      </Alert.Root>
    );
  }

  return (
    <VStack gap={8} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center">
        <Box>
          <Heading size="lg" mb={2} color="gray.800">{t('admin.content.userManagement.title')}</Heading>
          <Text color="gray.600">{t('admin.content.userManagement.subtitle')}</Text>
        </Box>
        <HStack gap={3}>
          <Button variant="outline">
            <FaDownload />
            {t('admin.content.userManagement.actions.export')}
          </Button>
          <Button colorPalette="blue">
            <FaUserPlus />
            {t('admin.content.userManagement.actions.addUser')}
          </Button>
        </HStack>
      </Flex>

      {/* User Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
        <Card.Root bg="white" shadow="md" borderTop="4px solid" borderColor="blue.500">
          <Card.Body>
            <Stat.Root>
              <Stat.Label>
                <HStack>
                  <Icon as={FaUsers} color="blue.500" />
                  <Text>{t('admin.content.userManagement.stats.totalUsers')}</Text>
                </HStack>
              </Stat.Label>
              <Stat.ValueText color="blue.600">{userStats.total}</Stat.ValueText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>

        <Card.Root bg="white" shadow="md" borderTop="4px solid" borderColor="green.500">
          <Card.Body>
            <Stat.Root>
              <Stat.Label>
                <HStack>
                  <Icon as={FaUserCheck} color="green.500" />
                  <Text>{t('admin.content.common.status.active')}</Text>
                </HStack>
              </Stat.Label>
              <Stat.ValueText color="green.600">{userStats.active}</Stat.ValueText>
              <Stat.HelpText>
                {userStats.total > 0 ? Math.round((userStats.active / userStats.total) * 100) : 0}% {t('admin.content.userManagement.stats.ofTotal')}
              </Stat.HelpText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>

        <Card.Root bg="white" shadow="md" borderTop="4px solid" borderColor="purple.500">
          <Card.Body>
            <Stat.Root>
              <Stat.Label>
                <HStack>
                  <Icon as={FaCrown} color="purple.500" />
                  <Text>{t('admin.content.userManagement.stats.admins')}</Text>
                </HStack>
              </Stat.Label>
              <Stat.ValueText color="purple.600">{userStats.admins}</Stat.ValueText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>

        <Card.Root bg="white" shadow="md" borderTop="4px solid" borderColor="orange.500">
          <Card.Body>
            <Stat.Root>
              <Stat.Label>
                <HStack>
                  <Icon as={FaUserCheck} color="orange.500" />
                  <Text>{t('admin.content.userManagement.stats.premium')}</Text>
                </HStack>
              </Stat.Label>
              <Stat.ValueText color="orange.600">{userStats.premium}</Stat.ValueText>
              <Stat.HelpText>
                {userStats.total > 0 ? Math.round((userStats.premium / userStats.total) * 100) : 0}% {t('admin.content.userManagement.stats.conversion')}
              </Stat.HelpText>
            </Stat.Root>
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      {/* Filters */}
      <Card.Root bg="white" shadow="md">
        <Card.Body>
          <VStack gap={4}>
            <VStack gap={4} w="full">
              <Box position="relative" maxW={{ base: "100%", md: "400px" }}>
                <Box position="absolute" left="3" top="50%" transform="translateY(-50%)" pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </Box>
                <Input
                  placeholder={t('admin.content.userManagement.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  paddingLeft="10"
                />
              </Box>
              
              <HStack gap={4} w="full" flexWrap="wrap">
                <Select.Root
                  value={statusFilter ? [statusFilter] : []}
                  onValueChange={(details) => setStatusFilter(details.value[0] || '')}
                  collection={createListCollection({
                    items: [
                      { value: 'active', label: t('admin.content.common.status.active') },
                      { value: 'inactive', label: t('admin.content.common.status.inactive') }
                    ]
                  })}
                  minW={{ base: "100%", sm: "150px" }}
                >
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('admin.content.common.allStatus')} />
                  </Select.Trigger>
                  <Select.Content>
                    {createListCollection({
                      items: [
                        { value: 'active', label: t('admin.content.common.status.active') },
                        { value: 'inactive', label: t('admin.content.common.status.inactive') }
                      ]
                    }).items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                
                <Select.Root
                  value={tierFilter ? [tierFilter] : []}
                  onValueChange={(details) => setTierFilter(details.value[0] || '')}
                  collection={createListCollection({
                    items: tiers.map((tier) => ({ value: tier, label: getTierLabel(tier, t, tierLabels[tier]) }))
                  })}
                  maxW="200px"
                >
                  <Select.Trigger>
                    <Select.ValueText placeholder={t('admin.content.common.allPlans')} />
                  </Select.Trigger>
                  <Select.Content>
                    {tiers.map((t) => (
                      <Select.Item key={t} item={{ value: t, label: tierLabels[t] || t }}>
                        {tierLabels[t] || t}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
                
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                    setTierFilter('');
                  }}
                  minW={{ base: "100%", sm: "auto" }}
                >
                  <FaFilter />
                  {t('admin.content.common.clear')}
                </Button>
              </HStack>
            </VStack>
            
            {(searchTerm || statusFilter || tierFilter) && (
              <HStack wrap="wrap" gap={2}>
                <Text fontSize="sm" color="gray.600">{t('admin.content.userManagement.activeFilters')}</Text>
                {searchTerm && (
                  <Tag.Root size="sm" colorPalette="blue">
                    <Tag.Label>{t('admin.content.userManagement.filters.search', { value: searchTerm })}</Tag.Label>
                    <Tag.CloseTrigger onClick={() => setSearchTerm('')} />
                  </Tag.Root>
                )}
                {statusFilter && (
                  <Tag.Root size="sm" colorPalette="green">
                    <Tag.Label>{t('admin.content.userManagement.filters.status', { value: statusFilter === 'active' ? t('admin.content.common.status.active') : t('admin.content.common.status.inactive') })}</Tag.Label>
                    <Tag.CloseTrigger onClick={() => setStatusFilter('')} />
                  </Tag.Root>
                )}
                {tierFilter && (
                  <Tag.Root size="sm" colorPalette="purple">
                    <Tag.Label>{t('admin.content.userManagement.filters.tier', { value: getTierLabel(tierFilter, t, tierLabels[tierFilter]) })}</Tag.Label>
                    <Tag.CloseTrigger onClick={() => setTierFilter('')} />
                  </Tag.Root>
                )}
              </HStack>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Users Table */}
      <Card.Root bg="white" shadow="md">
        <Card.Body>
          {/* Desktop Table View */}
          <Box display={{ base: 'none', lg: 'block' }}>
            <Table.Root variant="line">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader cursor="pointer" onClick={() => handleSort('email')} _hover={{ color: 'blue.600' }}>
                    {t('admin.content.userManagement.table.user')}{getSortIndicator('email')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader cursor="pointer" onClick={() => handleSort('is_active')} _hover={{ color: 'blue.600' }}>
                    {t('admin.content.userManagement.table.status')}{getSortIndicator('is_active')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader cursor="pointer" onClick={() => handleSort('subscription_tier')} _hover={{ color: 'blue.600' }}>
                    {t('admin.content.userManagement.table.subscription')}{getSortIndicator('subscription_tier')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader cursor="pointer" onClick={() => handleSort('is_superuser')} _hover={{ color: 'blue.600' }}>
                    {t('admin.content.userManagement.table.role')}{getSortIndicator('is_superuser')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader cursor="pointer" onClick={() => handleSort('last_login')} _hover={{ color: 'blue.600' }}>
                    {t('admin.content.userManagement.fields.lastLogin')}{getSortIndicator('last_login')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>{t('admin.content.userManagement.table.actions')}</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredUsers.map((user: any) => (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <HStack gap={3}>
                        <Avatar.Root size="sm">
                          <Avatar.Fallback>{user.email?.charAt(0).toUpperCase()}</Avatar.Fallback>
                        </Avatar.Root>
                        <VStack align="start" gap={1}>
                          <Text fontWeight="medium">{user.email}</Text>
                          <Text fontSize="sm" color="gray.500">{t('admin.content.userManagement.fields.userIdShort', { id: user.id })}</Text>
                        </VStack>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={getUserStatusColor(user.is_active ? 'active' : 'inactive')}
                        variant="subtle"
                        px={3}
                        py={1}
                      >
                        {user.is_active ? t('admin.content.common.status.active') : t('admin.content.common.status.inactive')}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={getSubscriptionColor(user.subscription_tier)}
                        variant="subtle"
                        px={3}
                        py={1}
                      >
                        {getTierLabel(user.subscription_tier, t)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={2}>
                        {user.is_superuser && (
                          <Badge colorPalette="red" variant="subtle">
                            {t('admin.content.userManagement.roles.admin')}
                          </Badge>
                        )}
                        <Badge colorPalette="gray" variant="outline">
                          {t('admin.content.userManagement.roles.user')}
                        </Badge>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : t('admin.content.common.never')}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <FaEllipsisV />
                          </IconButton>
                        </Menu.Trigger>
                        <Menu.Content>
                          <Menu.Item value="view"><FaEye /> {t('admin.content.userManagement.actions.viewDetails')}</Menu.Item>
                          <Menu.Item value="edit" onClick={() => openEditModal(user)}>
                            <FaEdit /> {t('admin.content.userManagement.actions.editUser')}
                          </Menu.Item>
                          <Menu.Item value="password" onClick={() => openPasswordChange(user)}>
                            <FaEdit /> {t('admin.content.userManagement.actions.changePassword')}
                          </Menu.Item>
                          <Separator />
                          <Menu.Item 
                            value="delete"
                            color="red.500"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <FaTrash /> {t('admin.content.userManagement.actions.deleteUser')}
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Root>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>

          {/* Mobile Card View */}
          <Box display={{ base: 'block', lg: 'none' }}>
            <VStack gap={4} align="stretch">
              {filteredUsers.map((user: any) => (
                <Card.Root key={user.id} variant="outline" p={4}>
                  <VStack gap={3} align="stretch">
                    <HStack justify="space-between">
                      <HStack gap={3}>
                        <Avatar.Root size="md">
                          <Avatar.Fallback>{user.email?.charAt(0).toUpperCase()}</Avatar.Fallback>
                        </Avatar.Root>
                        <VStack align="start" gap={1}>
                          <Text fontWeight="medium" fontSize="sm">{user.email}</Text>
                          <Text fontSize="xs" color="gray.500">{t('admin.content.userManagement.fields.userIdShort', { id: user.id })}</Text>
                        </VStack>
                      </HStack>
                      <Menu.Root>
                        <Menu.Trigger asChild>
                          <IconButton variant="ghost" size="sm">
                            <FaEllipsisV />
                          </IconButton>
                        </Menu.Trigger>
                        <Menu.Content>
                          <Menu.Item value="view"><FaEye /> {t('admin.content.userManagement.actions.viewDetails')}</Menu.Item>
                          <Menu.Item value="edit" onClick={() => openEditModal(user)}>
                            <FaEdit /> {t('admin.content.userManagement.actions.editUser')}
                          </Menu.Item>
                          <Menu.Item value="password" onClick={() => openPasswordChange(user)}>
                            <FaEdit /> {t('admin.content.userManagement.actions.changePassword')}
                          </Menu.Item>
                          <Separator />
                          <Menu.Item 
                            value="delete"
                            color="red.500"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <FaTrash /> {t('admin.content.userManagement.actions.deleteUser')}
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Root>
                    </HStack>
                    
                    <HStack justify="space-between" wrap="wrap" gap={2}>
                      <Badge
                        colorPalette={getUserStatusColor(user.is_active ? 'active' : 'inactive')}
                        variant="subtle"
                        px={2}
                        py={1}
                        fontSize="xs"
                      >
                        {user.is_active ? t('admin.content.common.status.active') : t('admin.content.common.status.inactive')}
                      </Badge>
                      <Badge
                        colorPalette={getSubscriptionColor(user.subscription_tier)}
                        variant="subtle"
                        px={2}
                        py={1}
                        fontSize="xs"
                      >
                        {getTierLabel(user.subscription_tier, t)}
                      </Badge>
                    </HStack>
                    
                    <HStack justify="space-between" wrap="wrap" gap={2}>
                      <HStack gap={2}>
                        {user.is_superuser && (
                          <Badge colorPalette="red" variant="subtle" fontSize="xs">
                            {t('admin.content.userManagement.roles.admin')}
                          </Badge>
                        )}
                        <Badge colorPalette="gray" variant="outline" fontSize="xs">
                          {t('admin.content.userManagement.roles.user')}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {t('admin.content.userManagement.fields.lastLoginShort', { value: user.last_login ? new Date(user.last_login).toLocaleDateString() : t('admin.content.common.never') })}
                      </Text>
                    </HStack>
                  </VStack>
                </Card.Root>
              ))}
            </VStack>
          </Box>
        </Card.Body>
      </Card.Root>

      {/* Edit User Modal */}
      <UserEditModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        user={selectedUser}
        onUpdate={handleUpdateUser}
      />

      {/* Password Change Modal */}
      <DialogRoot open={isPasswordOpen} onOpenChange={(e) => setIsPasswordOpen(e.open)} size="md" placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <DialogContent 
          maxH="90vh" 
          position="fixed" 
          top="50%" 
          left="50%" 
          transform="translate(-50%, -50%)"
        >
          <DialogHeader>{t('admin.content.userManagement.changePasswordTitle', { email: passwordChangeUser?.email })}</DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <VStack gap={4} align="stretch">
              <Field.Root>
                <Field.Label>{t('admin.content.userManagement.fields.newPassword')}</Field.Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </Field.Root>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsPasswordOpen(false)}>
              {t('admin.content.common.cancel')}
            </Button>
            <Button
              colorPalette="blue"
              onClick={handlePasswordChange}
            >
              {t('admin.content.userManagement.actions.changePassword')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </VStack>
  );
};
